import * as Sentry from '@sentry/browser'
import api from '../api'
import { loadInitialData } from './initialData'
import { clearUserAndSendToLogin } from './logoutInternal'
import { generateClientIdentityToken } from './clientIdentity'
import analytics from '../utils/cdpAnalytics'

function setCurrentUser(dispatch, response) {
  // save session in local storage
  localStorage.setItem('ls.session', JSON.stringify(response))
  // call identify to set userId for analytics tracking
  analytics.identify(response.user, {})
  dispatch({ type: 'AUTHENTICATION_SUCCESS', response })
  return null
}

export function login(data) {
  return commonLogin.bind(null, '/sessions', data)
}

export function externalLogin(data, doNotCreateUser) {
  let relativePath = '/sessions/external-signin'
  if (doNotCreateUser) { relativePath += '?doNotCreateUser=true' }
  return commonLogin.bind(null, relativePath, data)
}

async function commonLogin(path, data, dispatch, getState) {
  ensureClientIdentityToken(dispatch, getState)
  try {
    const response = await api.post(path, data)
    return handleLoginSuccess(dispatch, response)
  } catch (error) {
    // handle authFlow required case
    if (
      error.response?.status === 422 &&
      error.response?.textString.includes('authFlow required')
    ) {
      const authFlow = await api.post(
        '/authFlows',
        { email: data.email, password: data.password },
      )
      if (authFlow.status === 'succeeded') {
        const session = await api.post('/sessions/authFlow', { token: authFlow.token })
        return handleLoginSuccess(dispatch, session)
      }
      const emailOtpStep = authFlow.nextSteps?.find((it) => it.eventName === 'email-otp')
      if (!emailOtpStep) {
        Sentry.withScope((scope) => {
          scope.setExtra('authFlow', authFlow)
          scope.setExtra('nextSteps', authFlow.nextSteps)
          Sentry.captureException(new Error('email-otp step not found in authFlow nextSteps'))
        })
        throw new Error('Unexpected authentication flow')
      }
      return {
        authFlow: { ...authFlow, emailOtpEventId: emailOtpStep.eventId },
      }
    }
    throw error
  }
}

function ensureClientIdentityToken(dispatch, getState) {
  const { clientIdentity } = getState()
  if (!clientIdentity) {
    generateClientIdentityToken(dispatch)
  }
}

function handleLoginSuccess(dispatch, response) {
  dispatch({ type: 'LOGIN_SUCCESS', response })
  setCurrentUser(dispatch, response)
  dispatch(loadInitialData(response.user))
  return response
}

export function resendEmailOtp(authFlowId, data) {
  return () => api.post(`/authFlows/${authFlowId}/resend`, data)
}

export function verifyEmailOtp(eventId, otpCode) {
  return async (dispatch) => {
    const authFlow = await api.post(
      `/authFlows/authFlowEvents/${eventId}/verify`,
      { code: otpCode },
    )
    if (authFlow.status === 'succeeded') {
      const session = await api.post('/sessions/authFlow', { token: authFlow.token })
      return handleLoginSuccess(dispatch, session)
    }
    Sentry.withScope((scope) => {
      scope.setExtra('authFlow', authFlow)
      scope.setExtra('authFlowStatus', authFlow.status)
      Sentry.captureException(new Error('unknown authFlow situation: OTP accepted but authFlow status not succeeded'))
    })
    throw new Error('unknown authFlow situation')
  }
}

export function getAuthFlowEvent(eventId, code) {
  return () => api.fetch(`/authFlows/authFlowEvents/${eventId}?code=${code}`)
}

export function verifyMobileSignin(eventId, code) {
  return () => api.post(
    `/authFlows/authFlowEvents/${eventId}/verify`,
    { code },
  )
}

export function signup(data) {
  return () => api.post('/users', data)
    .then((response) => response)
}

export function logout() {
  return (dispatch) => api.delete('/sessions')
    .then(() => clearUserAndSendToLogin(dispatch))
    .catch(() => {})
}

export function authenticate() {
  const url = '/sessions/self'
  localStorage.removeItem('plickers-tab-sync')
  return (dispatch) => {
    dispatch({ type: 'AUTHENTICATION_REQUEST' })
    return api.fetch(url)
      .then((response) => {
        setCurrentUser(dispatch, response)
        return response
      })
      .catch(() => clearUserAndSendToLogin(dispatch))
  }
}

export const unauthenticate = () => ({ type: 'AUTHENTICATION_FAILURE' })
